import { Plus, X } from "lucide-react";
import { useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";

import { useWorkflows } from "../context/WorkflowsContext";
import { pickCounts } from "../forms";
import { FormPicker } from "./FormPicker";

/** Every form CaseBlink fills; picking one adds another copy, even of a picked one. */
const AddForm: FC = () => {
  const w = useWorkflows();
  const [open, setOpen] = useState(false);
  const counts = pickCounts(w.formPicks ?? []);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          className="self-start"
          disabled={!w.formCatalog?.length}
          data-testid="workflow-forms-add"
        >
          <Plus />
          Add Form
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <FormPicker
          catalog={w.formCatalog ?? []}
          counts={counts}
          onPick={(f) => {
            w.addForm(f);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

/** Fill Forms: the copies of forms to fill, each its own form on the case. */
export const FormsOptions: FC = () => {
  const w = useWorkflows();
  if (w.formCatalog === null)
    return (
      <div className="flex flex-col gap-1.5" aria-label="Loading forms">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    );
  if (w.formPicks === null)
    return (
      <p className="text-sm text-fg-3">
        Casey fills the USCIS forms a {w.settings.visa || "this visa"} filing
        needs, plus the G-28.
      </p>
    );
  const counts = pickCounts(w.formPicks);
  return (
    <fieldset className="flex flex-col gap-1.5" data-testid="workflow-forms">
      <legend className="mb-1.5 text-sm font-medium text-fg-2">Forms</legend>
      {w.formPicks.length ? null : (
        <p className="text-sm text-fg-3">
          No forms picked. Add the ones this case files.
        </p>
      )}
      {w.formPicks.map((p, i) => {
        const choice = w.forms[i];
        const name = choice?.name ?? p.formNumber;
        const named = !p.formId && (counts.get(p.formNumber) ?? 0) > 1;
        return (
          <div
            key={p.id}
            className="flex items-center gap-2"
            data-testid="workflow-forms-pick"
          >
            <span
              className="min-w-0 flex-1 truncate text-base text-fg"
              title={p.title}
            >
              {name}
            </span>
            {named ? (
              <Input
                value={p.note}
                onChange={(e) => w.setFormNote(p.id, e.target.value)}
                placeholder="Label, e.g. Beneficiary"
                aria-label={`${name} Label`}
                maxLength={40}
                className="h-6 w-36 text-sm"
              />
            ) : null}
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Remove ${name}`}
              title={`Remove ${name}`}
              onClick={() => w.removeForm(p.id)}
            >
              <X />
            </Button>
          </div>
        );
      })}
      <AddForm />
    </fieldset>
  );
};
